import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, LogOut, Calendar, Users, Clock, Check, X, Image, Phone, Mail, Upload, Link, Scissors, GraduationCap, DollarSign } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [scheduleFormData, setScheduleFormData] = useState({
    course_title_en: '',
    course_title_ar: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    spots_available: 10,
  });
  const [galleryFormData, setGalleryFormData] = useState({
    url: '',
    caption_en: '',
    caption_ar: '',
    category: 'work',
  });
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const fileInputRef = useRef(null);
  const serviceFileInputRef = useRef(null);
  const courseFileInputRef = useRef(null);

  useEffect(() => {
    const savedLogin = localStorage.getItem('nina-admin-logged-in');
    if (savedLogin === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const fetchData = async () => {
    try {
      const [schedulesRes, bookingsRes, galleryRes, servicesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/admin/schedule`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/admin/services`),
        axios.get(`${API}/admin/courses`),
      ]);
      setSchedules(schedulesRes.data);
      setBookings(bookingsRes.data);
      setGallery(galleryRes.data);
      setServices(servicesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      if (response.data.success) {
        setIsLoggedIn(true);
        localStorage.setItem('nina-admin-logged-in', 'true');
        toast.success(language === 'en' ? 'Login successful' : 'تم تسجيل الدخول بنجاح');
      }
    } catch (error) {
      toast.error(language === 'en' ? 'Invalid credentials' : 'بيانات غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('nina-admin-logged-in');
    setCredentials({ username: '', password: '' });
  };

  // Schedule Functions
  const handleSubmitSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSchedule) {
        await axios.put(`${API}/admin/schedule/${editingSchedule.id}`, scheduleFormData);
        toast.success(language === 'en' ? 'Schedule updated' : 'تم تحديث الجدول');
      } else {
        await axios.post(`${API}/admin/schedule`, scheduleFormData);
        toast.success(language === 'en' ? 'Schedule added' : 'تمت إضافة الجدول');
      }
      fetchData();
      resetScheduleForm();
    } catch (error) {
      toast.error(language === 'en' ? 'Operation failed' : 'فشلت العملية');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this schedule?' : 'هل أنت متأكد من حذف هذا الجدول؟')) return;

    try {
      await axios.delete(`${API}/admin/schedule/${id}`);
      toast.success(language === 'en' ? 'Schedule deleted' : 'تم حذف الجدول');
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Delete failed' : 'فشل الحذف');
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await axios.put(`${API}/admin/schedule/${schedule.id}`, {
        is_active: !schedule.is_active,
      });
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Update failed' : 'فشل التحديث');
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleFormData({
      course_title_en: schedule.course_title_en,
      course_title_ar: schedule.course_title_ar,
      start_date: schedule.start_date,
      end_date: schedule.end_date,
      start_time: schedule.start_time || '09:00',
      end_time: schedule.end_time || '17:00',
      spots_available: schedule.spots_available,
    });
    setShowScheduleForm(true);
  };

  const resetScheduleForm = () => {
    setEditingSchedule(null);
    setShowScheduleForm(false);
    setScheduleFormData({
      course_title_en: '',
      course_title_ar: '',
      start_date: '',
      end_date: '',
      start_time: '09:00',
      end_time: '17:00',
      spots_available: 10,
    });
  };

  // Booking Functions
  const handleBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status });
      toast.success(language === 'en' ? `Booking ${status}` : `تم ${status === 'accepted' ? 'قبول' : 'إلغاء'} الحجز`);
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Update failed' : 'فشل التحديث');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this booking?' : 'هل أنت متأكد من حذف هذا الحجز؟')) return;

    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success(language === 'en' ? 'Booking deleted' : 'تم حذف الحجز');
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Delete failed' : 'فشل الحذف');
    }
  };

  // Gallery Functions
  const handleSubmitGallery = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/gallery`, galleryFormData);
      toast.success(language === 'en' ? 'Image added to gallery' : 'تمت إضافة الصورة للمعرض');
      fetchData();
      resetGalleryForm();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to add image' : 'فشل إضافة الصورة');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(
        language === 'en' 
          ? `${response.data.uploaded} photo(s) uploaded successfully` 
          : `تم رفع ${response.data.uploaded} صورة بنجاح`
      );
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(language === 'en' ? 'Failed to upload images' : 'فشل رفع الصور');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    handleFileUpload(files);
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this image?' : 'هل أنت متأكد من حذف هذه الصورة؟')) return;

    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`);
      toast.success(language === 'en' ? 'Image deleted' : 'تم حذف الصورة');
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Delete failed' : 'فشل الحذف');
    }
  };

  const handleUpdateImageCategory = async (imageId, newCategory) => {
    try {
      await axios.put(`${API}/admin/gallery/${imageId}`, { category: newCategory });
      toast.success(language === 'en' ? 'Category updated' : 'تم تحديث الفئة');
      setEditingImage(null);
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Update failed' : 'فشل التحديث');
    }
  };

  const handleUpdateImageCaption = async (imageId, captionEn, captionAr) => {
    try {
      await axios.put(`${API}/admin/gallery/${imageId}`, { 
        caption_en: captionEn, 
        caption_ar: captionAr 
      });
      toast.success(language === 'en' ? 'Caption updated' : 'تم تحديث الوصف');
      setEditingImage(null);
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Update failed' : 'فشل التحديث');
    }
  };

  const resetGalleryForm = () => {
    setShowGalleryForm(false);
    setGalleryFormData({
      url: '',
      caption_en: '',
      caption_ar: '',
      category: 'work',
    });
    setUploadMode('file');
  };

  // Service Functions
  const handleUpdateService = async (serviceId, updateData) => {
    try {
      await axios.put(`${API}/admin/services/${serviceId}`, updateData);
      toast.success(language === 'en' ? 'Service updated' : 'تم تحديث الخدمة');
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Update failed' : 'فشل التحديث');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this service?' : 'هل أنت متأكد من حذف هذه الخدمة؟')) return;

    try {
      await axios.delete(`${API}/admin/services/${serviceId}`);
      toast.success(language === 'en' ? 'Service deleted' : 'تم حذف الخدمة');
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Delete failed' : 'فشل الحذف');
    }
  };

  const handleServiceImageUpload = async (files, serviceId) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('files', files[0]);
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Backend returns { images: [{ url: '...' }] }
      if (response.data.images && response.data.images.length > 0) {
        const imageUrl = response.data.images[0].url;
        // Convert relative URL to full URL for storage
        const fullUrl = imageUrl.startsWith('/api/') 
          ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`
          : imageUrl;
        await handleUpdateService(serviceId, { image_url: fullUrl });
        toast.success(language === 'en' ? 'Photo updated!' : 'تم تحديث الصورة!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(language === 'en' ? 'Failed to upload image' : 'فشل رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  // Course Functions
  const handleUpdateCourse = async (courseId, updateData) => {
    try {
      await axios.put(`${API}/admin/courses/${courseId}`, updateData);
      toast.success(language === 'en' ? 'Course updated' : 'تم تحديث الدورة');
      setEditingCourse(null);
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Update failed' : 'فشل التحديث');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this course?' : 'هل أنت متأكد من حذف هذه الدورة؟')) return;

    try {
      await axios.delete(`${API}/admin/courses/${courseId}`);
      toast.success(language === 'en' ? 'Course deleted' : 'تم حذف الدورة');
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Delete failed' : 'فشل الحذف');
    }
  };

  const handleCourseImageUpload = async (files, courseId) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('files', files[0]);
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Backend returns { images: [{ url: '...' }] }
      if (response.data.images && response.data.images.length > 0) {
        const imageUrl = response.data.images[0].url;
        // Convert relative URL to full URL for storage
        const fullUrl = imageUrl.startsWith('/api/') 
          ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`
          : imageUrl;
        await handleUpdateCourse(courseId, { image_url: fullUrl });
        toast.success(language === 'en' ? 'Photo updated!' : 'تم تحديث الصورة!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(language === 'en' ? 'Failed to upload image' : 'فشل رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  };

  const getStatusText = (status) => {
    if (language === 'en') {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
    switch (status) {
      case 'accepted':
        return 'مقبول';
      case 'cancelled':
        return 'ملغي';
      default:
        return 'قيد الانتظار';
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-[#E6E2D6]">
          <CardHeader className="text-center">
            <CardTitle className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-2xl text-[#1A1A1A]`}>
              {t.admin.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="username">{t.admin.username}</Label>
                <Input
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                  data-testid="admin-username"
                  className="border-[#E6E2D6] focus:border-[#C5A059]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t.admin.password}</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  data-testid="admin-password"
                  className="border-[#E6E2D6] focus:border-[#C5A059]"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                className="w-full bg-[#C5A059] hover:bg-[#B08A3E] rounded-full py-6"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t.admin.loginBtn
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#FDFCF8] pt-24">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1
            data-testid="admin-title"
            className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-3xl font-medium text-[#1A1A1A]`}
          >
            {t.admin.title}
          </h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="admin-logout-btn"
            className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-full"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t.admin.logout}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-[#F3ECE4] p-1 rounded-full flex-wrap">
            <TabsTrigger
              value="bookings"
              data-testid="tab-bookings"
              className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-white px-6"
            >
              {language === 'en' ? 'Bookings' : 'الحجوزات'} ({bookings.length})
            </TabsTrigger>
            <TabsTrigger
              value="services"
              data-testid="tab-services"
              className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-white px-6"
            >
              {language === 'en' ? 'Services' : 'الخدمات'} ({services.length})
            </TabsTrigger>
            <TabsTrigger
              value="courses"
              data-testid="tab-courses"
              className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-white px-6"
            >
              {language === 'en' ? 'Courses' : 'الدورات'} ({courses.length})
            </TabsTrigger>
            <TabsTrigger
              value="schedule"
              data-testid="tab-schedule"
              className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-white px-6"
            >
              {language === 'en' ? 'Schedule' : 'الجدول'} ({schedules.length})
            </TabsTrigger>
            <TabsTrigger
              value="gallery"
              data-testid="tab-gallery"
              className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-white px-6"
            >
              {language === 'en' ? 'Gallery' : 'المعرض'} ({gallery.length})
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-6">
            <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-xl font-medium text-[#1A1A1A]`}>
              {language === 'en' ? 'Customer Bookings' : 'حجوزات العملاء'}
            </h2>

            {bookings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-[#E6E2D6]">
                <Calendar className="w-12 h-12 text-[#C5A059]/50 mx-auto mb-4" />
                <p className="text-[#666660]">{language === 'en' ? 'No bookings yet' : 'لا توجد حجوزات بعد'}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {bookings.map((booking, index) => (
                  <Card
                    key={booking.id}
                    data-testid={`booking-card-${index}`}
                    className="border-[#E6E2D6] hover:border-[#C5A059]/30 transition-all"
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Booking Info */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-playfair text-lg font-medium text-[#1A1A1A]">
                              {booking.name}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                              {getStatusText(booking.status)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#666660]">
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-[#C5A059]" />
                              <a href={`tel:${booking.phone}`} className="hover:text-[#C5A059]">
                                {booking.phone}
                              </a>
                            </div>
                            {booking.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-[#C5A059]" />
                                <a href={`mailto:${booking.email}`} className="hover:text-[#C5A059]">
                                  {booking.email}
                                </a>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-[#C5A059]" />
                              {formatDate(booking.preferred_date)}
                              {booking.preferred_time && ` at ${booking.preferred_time}`}
                            </div>
                          </div>

                          <div className="bg-[#F3ECE4]/50 px-4 py-2 rounded-lg inline-block">
                            <span className="text-sm text-[#666660]">
                              {booking.service_type === 'course' ? (language === 'en' ? 'Course: ' : 'دورة: ') : (language === 'en' ? 'Service: ' : 'خدمة: ')}
                            </span>
                            <span className="text-sm font-medium text-[#1A1A1A]">{booking.course_or_service}</span>
                          </div>

                          {booking.message && (
                            <p className="text-sm text-[#666660] italic">"{booking.message}"</p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          {booking.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleBookingStatus(booking.id, 'accepted')}
                                data-testid={`accept-booking-${index}`}
                                className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                {language === 'en' ? 'Accept' : 'قبول'}
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleBookingStatus(booking.id, 'cancelled')}
                                data-testid={`cancel-booking-${index}`}
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
                              >
                                <X className="w-4 h-4 mr-1" />
                                {language === 'en' ? 'Cancel' : 'إلغاء'}
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteBooking(booking.id)}
                            data-testid={`delete-booking-${index}`}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-6">
            <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-xl font-medium text-[#1A1A1A]`}>
              {language === 'en' ? 'Manage Services' : 'إدارة الخدمات'}
            </h2>

            {services.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-[#E6E2D6]">
                <Scissors className="w-12 h-12 text-[#C5A059]/50 mx-auto mb-4" />
                <p className="text-[#666660]">{language === 'en' ? 'No services yet' : 'لا توجد خدمات بعد'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service, index) => (
                  <Card
                    key={service.id}
                    data-testid={`admin-service-${index}`}
                    className="border-[#E6E2D6] hover:border-[#C5A059]/30 transition-all overflow-hidden"
                  >
                    {/* Service Image */}
                    <div className="aspect-video relative group">
                      <img
                        src={getImageUrl(service.image_url)}
                        alt={language === 'en' ? service.name_en : service.name_ar}
                        className="w-full h-full object-cover"
                      />
                      {/* Image Upload Overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleServiceImageUpload(e.target.files, service.id)}
                          />
                          <div className="bg-white text-[#1A1A1A] px-4 py-2 rounded-full flex items-center gap-2 hover:bg-[#C5A059] hover:text-white transition-colors">
                            <Upload className="w-4 h-4" />
                            {language === 'en' ? 'Change Photo' : 'تغيير الصورة'}
                          </div>
                        </label>
                      </div>
                    </div>

                    <CardContent className="p-4 space-y-3">
                      {/* Service Name & Price */}
                      <div className="flex justify-between items-start">
                        <h3 className="font-playfair text-lg font-medium text-[#1A1A1A]">
                          {language === 'en' ? service.name_en : service.name_ar}
                        </h3>
                        <span className="bg-[#C5A059]/10 text-[#C5A059] px-3 py-1 rounded-full text-sm font-medium">
                          {service.price}
                        </span>
                      </div>

                      {/* Description Preview */}
                      <p className="text-sm text-[#666660] line-clamp-2">
                        {language === 'en' ? service.description_en : service.description_ar}
                      </p>

                      {/* Duration */}
                      <div className="flex items-center gap-2 text-sm text-[#666660]">
                        <Clock className="w-4 h-4 text-[#C5A059]" />
                        {service.duration}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingService(service)}
                          data-testid={`edit-service-${index}`}
                          className="flex-1 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-full"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          {language === 'en' ? 'Edit' : 'تعديل'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteService(service.id)}
                          data-testid={`delete-service-${index}`}
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Edit Service Modal */}
            {editingService && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingService(null)}>
                <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-playfair text-xl font-medium text-[#1A1A1A] mb-6">
                    {language === 'en' ? 'Edit Service' : 'تعديل الخدمة'}
                  </h3>
                  
                  {/* Preview Image */}
                  <div className="aspect-video rounded-lg overflow-hidden mb-4 relative group">
                    <img
                      src={getImageUrl(editingService.image_url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleServiceImageUpload(e.target.files, editingService.id)}
                        />
                        <div className="bg-white text-[#1A1A1A] px-4 py-2 rounded-full flex items-center gap-2 hover:bg-[#C5A059] hover:text-white transition-colors">
                          <Upload className="w-4 h-4" />
                          {language === 'en' ? 'Change Photo' : 'تغيير الصورة'}
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Edit Form */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Name (English)' : 'الاسم (إنجليزي)'}</Label>
                        <Input
                          defaultValue={editingService.name_en}
                          id="edit-service-name-en"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Name (Arabic)' : 'الاسم (عربي)'}</Label>
                        <Input
                          defaultValue={editingService.name_ar}
                          id="edit-service-name-ar"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                          dir="rtl"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm">{language === 'en' ? 'Description (English)' : 'الوصف (إنجليزي)'}</Label>
                      <textarea
                        defaultValue={editingService.description_en}
                        id="edit-service-desc-en"
                        rows={3}
                        className="w-full border border-[#E6E2D6] focus:border-[#C5A059] rounded-md p-2 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm">{language === 'en' ? 'Description (Arabic)' : 'الوصف (عربي)'}</Label>
                      <textarea
                        defaultValue={editingService.description_ar}
                        id="edit-service-desc-ar"
                        rows={3}
                        className="w-full border border-[#E6E2D6] focus:border-[#C5A059] rounded-md p-2 mt-1"
                        dir="rtl"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Price' : 'السعر'}</Label>
                        <Input
                          defaultValue={editingService.price}
                          id="edit-service-price"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                          placeholder="500 SAR"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Duration' : 'المدة'}</Label>
                        <Input
                          defaultValue={editingService.duration}
                          id="edit-service-duration"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                          placeholder="60 min"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => {
                        handleUpdateService(editingService.id, {
                          name_en: document.getElementById('edit-service-name-en').value,
                          name_ar: document.getElementById('edit-service-name-ar').value,
                          description_en: document.getElementById('edit-service-desc-en').value,
                          description_ar: document.getElementById('edit-service-desc-ar').value,
                          price: document.getElementById('edit-service-price').value,
                          duration: document.getElementById('edit-service-duration').value,
                        });
                      }}
                      className="flex-1 bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
                    >
                      {language === 'en' ? 'Save Changes' : 'حفظ التغييرات'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingService(null)}
                      className="border-[#E6E2D6] rounded-full"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-xl font-medium text-[#1A1A1A]`}>
              {language === 'en' ? 'Manage Courses' : 'إدارة الدورات'}
            </h2>

            {courses.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-[#E6E2D6]">
                <GraduationCap className="w-12 h-12 text-[#C5A059]/50 mx-auto mb-4" />
                <p className="text-[#666660]">{language === 'en' ? 'No courses yet' : 'لا توجد دورات بعد'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {courses.map((course, index) => (
                  <Card
                    key={course.id}
                    data-testid={`admin-course-${index}`}
                    className="border-[#E6E2D6] hover:border-[#C5A059]/30 transition-all overflow-hidden"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Course Image */}
                      <div className="md:w-1/3 aspect-video md:aspect-square relative group">
                        <img
                          src={getImageUrl(course.image_url)}
                          alt={language === 'en' ? course.title_en : course.title_ar}
                          className="w-full h-full object-cover"
                        />
                        {/* Image Upload Overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleCourseImageUpload(e.target.files, course.id)}
                            />
                            <div className="bg-white text-[#1A1A1A] px-3 py-2 rounded-full flex items-center gap-2 text-sm hover:bg-[#C5A059] hover:text-white transition-colors">
                              <Upload className="w-4 h-4" />
                              {language === 'en' ? 'Change' : 'تغيير'}
                            </div>
                          </label>
                        </div>
                      </div>

                      <CardContent className="md:w-2/3 p-4 space-y-3">
                        {/* Course Title */}
                        <h3 className="font-playfair text-lg font-medium text-[#1A1A1A]">
                          {language === 'en' ? course.title_en : course.title_ar}
                        </h3>

                        {/* Prices */}
                        <div className="flex gap-3">
                          <span className="bg-[#C5A059]/10 text-[#C5A059] px-3 py-1 rounded-full text-xs font-medium">
                            {language === 'en' ? 'Group' : 'مجموعة'}: {course.group_price} SAR
                          </span>
                          <span className="bg-[#1A1A1A]/10 text-[#1A1A1A] px-3 py-1 rounded-full text-xs font-medium">
                            {language === 'en' ? 'Individual' : 'فردي'}: {course.individual_price} SAR
                          </span>
                        </div>

                        {/* Description Preview */}
                        <p className="text-sm text-[#666660] line-clamp-2">
                          {language === 'en' ? course.description_en : course.description_ar}
                        </p>

                        {/* Duration */}
                        <div className="flex items-center gap-2 text-sm text-[#666660]">
                          <Clock className="w-4 h-4 text-[#C5A059]" />
                          {course.duration}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingCourse(course)}
                            data-testid={`edit-course-${index}`}
                            className="flex-1 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-full"
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            {language === 'en' ? 'Edit' : 'تعديل'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteCourse(course.id)}
                            data-testid={`delete-course-${index}`}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Edit Course Modal */}
            {editingCourse && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingCourse(null)}>
                <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-playfair text-xl font-medium text-[#1A1A1A] mb-6">
                    {language === 'en' ? 'Edit Course' : 'تعديل الدورة'}
                  </h3>
                  
                  {/* Preview Image */}
                  <div className="aspect-video rounded-lg overflow-hidden mb-4 relative group">
                    <img
                      src={getImageUrl(editingCourse.image_url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleCourseImageUpload(e.target.files, editingCourse.id)}
                        />
                        <div className="bg-white text-[#1A1A1A] px-4 py-2 rounded-full flex items-center gap-2 hover:bg-[#C5A059] hover:text-white transition-colors">
                          <Upload className="w-4 h-4" />
                          {language === 'en' ? 'Change Photo' : 'تغيير الصورة'}
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Edit Form */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Title (English)' : 'العنوان (إنجليزي)'}</Label>
                        <Input
                          defaultValue={editingCourse.title_en}
                          id="edit-course-title-en"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Title (Arabic)' : 'العنوان (عربي)'}</Label>
                        <Input
                          defaultValue={editingCourse.title_ar}
                          id="edit-course-title-ar"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                          dir="rtl"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm">{language === 'en' ? 'Description (English)' : 'الوصف (إنجليزي)'}</Label>
                      <textarea
                        defaultValue={editingCourse.description_en}
                        id="edit-course-desc-en"
                        rows={3}
                        className="w-full border border-[#E6E2D6] focus:border-[#C5A059] rounded-md p-2 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm">{language === 'en' ? 'Description (Arabic)' : 'الوصف (عربي)'}</Label>
                      <textarea
                        defaultValue={editingCourse.description_ar}
                        id="edit-course-desc-ar"
                        rows={3}
                        className="w-full border border-[#E6E2D6] focus:border-[#C5A059] rounded-md p-2 mt-1"
                        dir="rtl"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Group Price (SAR)' : 'سعر المجموعة'}</Label>
                        <Input
                          type="number"
                          defaultValue={editingCourse.group_price}
                          id="edit-course-group-price"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Individual Price (SAR)' : 'السعر الفردي'}</Label>
                        <Input
                          type="number"
                          defaultValue={editingCourse.individual_price}
                          id="edit-course-individual-price"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{language === 'en' ? 'Duration' : 'المدة'}</Label>
                        <Input
                          defaultValue={editingCourse.duration}
                          id="edit-course-duration"
                          className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                          placeholder="2 Days"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => {
                        handleUpdateCourse(editingCourse.id, {
                          title_en: document.getElementById('edit-course-title-en').value,
                          title_ar: document.getElementById('edit-course-title-ar').value,
                          description_en: document.getElementById('edit-course-desc-en').value,
                          description_ar: document.getElementById('edit-course-desc-ar').value,
                          group_price: parseInt(document.getElementById('edit-course-group-price').value),
                          individual_price: parseInt(document.getElementById('edit-course-individual-price').value),
                          duration: document.getElementById('edit-course-duration').value,
                        });
                      }}
                      className="flex-1 bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
                    >
                      {language === 'en' ? 'Save Changes' : 'حفظ التغييرات'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingCourse(null)}
                      className="border-[#E6E2D6] rounded-full"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-xl font-medium text-[#1A1A1A]`}>
                {t.admin.manageSchedule}
              </h2>
              <Button
                onClick={() => setShowScheduleForm(true)}
                data-testid="add-schedule-btn"
                className="bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.admin.addSchedule}
              </Button>
            </div>

            {/* Schedule Form */}
            {showScheduleForm && (
              <Card className="border-[#C5A059]/30 bg-[#C5A059]/5">
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmitSchedule} className="space-y-4" data-testid="schedule-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t.admin.courseTitleEn}</Label>
                        <Input
                          value={scheduleFormData.course_title_en}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, course_title_en: e.target.value })}
                          required
                          data-testid="input-course-title-en"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                          placeholder="Manicure Masterclass"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t.admin.courseTitleAr}</Label>
                        <Input
                          value={scheduleFormData.course_title_ar}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, course_title_ar: e.target.value })}
                          required
                          data-testid="input-course-title-ar"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                          placeholder="ماستر كلاس المانيكير"
                          dir="rtl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t.admin.startDate}</Label>
                        <Input
                          type="date"
                          value={scheduleFormData.start_date}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, start_date: e.target.value })}
                          required
                          data-testid="input-start-date"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t.admin.endDate}</Label>
                        <Input
                          type="date"
                          value={scheduleFormData.end_date}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, end_date: e.target.value })}
                          required
                          data-testid="input-end-date"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'en' ? 'Start Time' : 'وقت البدء'}</Label>
                        <Input
                          type="time"
                          value={scheduleFormData.start_time}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, start_time: e.target.value })}
                          required
                          data-testid="input-start-time"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'en' ? 'End Time' : 'وقت الانتهاء'}</Label>
                        <Input
                          type="time"
                          value={scheduleFormData.end_time}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, end_time: e.target.value })}
                          required
                          data-testid="input-end-time"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t.admin.spots}</Label>
                        <Input
                          type="number"
                          min="1"
                          value={scheduleFormData.spots_available}
                          onChange={(e) => setScheduleFormData({ ...scheduleFormData, spots_available: parseInt(e.target.value) })}
                          required
                          data-testid="input-spots"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Button
                        type="submit"
                        disabled={loading}
                        data-testid="save-schedule-btn"
                        className="bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
                      >
                        {t.admin.save}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetScheduleForm}
                        data-testid="cancel-schedule-btn"
                        className="border-[#E6E2D6] rounded-full"
                      >
                        {t.admin.cancel}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Schedule List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {schedules.map((schedule, index) => (
                <Card
                  key={schedule.id}
                  data-testid={`admin-schedule-${index}`}
                  className={`border-[#E6E2D6] ${!schedule.is_active ? 'opacity-60' : ''}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="font-playfair text-lg font-medium text-[#1A1A1A]">
                          {language === 'en' ? schedule.course_title_en : schedule.course_title_ar}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-sm text-[#666660]">
                          <Calendar className="w-4 h-4 text-[#C5A059]" />
                          {formatDate(schedule.start_date)} - {formatDate(schedule.end_date)}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-[#666660]">
                          <Clock className="w-4 h-4 text-[#C5A059]" />
                          {schedule.start_time || '09:00'} - {schedule.end_time || '17:00'}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-[#666660]">
                          <Users className="w-4 h-4 text-[#C5A059]" />
                          {schedule.spots_available} {t.schedule.spotsAvailable}
                        </div>
                      </div>
                      <Switch
                        checked={schedule.is_active}
                        onCheckedChange={() => handleToggleActive(schedule)}
                        data-testid={`toggle-active-${index}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditSchedule(schedule)}
                        data-testid={`edit-schedule-${index}`}
                        className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-full"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        {language === 'en' ? 'Edit' : 'تعديل'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        data-testid={`delete-schedule-${index}`}
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {language === 'en' ? 'Delete' : 'حذف'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-xl font-medium text-[#1A1A1A]`}>
                {language === 'en' ? 'Manage Gallery' : 'إدارة المعرض'}
              </h2>
              <Button
                onClick={() => setShowGalleryForm(true)}
                data-testid="add-gallery-btn"
                className="bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'en' ? 'Add Photos' : 'إضافة صور'}
              </Button>
            </div>

            {/* Gallery Upload Form */}
            {showGalleryForm && (
              <Card className="border-[#C5A059]/30 bg-[#C5A059]/5">
                <CardContent className="pt-6">
                  {/* Upload Mode Toggle */}
                  <div className="flex gap-2 mb-6">
                    <Button
                      type="button"
                      variant={uploadMode === 'file' ? 'default' : 'outline'}
                      onClick={() => setUploadMode('file')}
                      className={uploadMode === 'file' 
                        ? 'bg-[#C5A059] hover:bg-[#B08A3E] rounded-full' 
                        : 'border-[#C5A059] text-[#C5A059] rounded-full'
                      }
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {language === 'en' ? 'Upload Files' : 'رفع ملفات'}
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === 'url' ? 'default' : 'outline'}
                      onClick={() => setUploadMode('url')}
                      className={uploadMode === 'url' 
                        ? 'bg-[#C5A059] hover:bg-[#B08A3E] rounded-full' 
                        : 'border-[#C5A059] text-[#C5A059] rounded-full'
                      }
                    >
                      <Link className="w-4 h-4 mr-2" />
                      {language === 'en' ? 'Add by URL' : 'إضافة برابط'}
                    </Button>
                  </div>

                  {uploadMode === 'file' ? (
                    /* File Upload Mode */
                    <div className="space-y-4">
                      {/* Drag & Drop Zone */}
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-300 ${
                          isDragging 
                            ? 'border-[#C5A059] bg-[#C5A059]/10' 
                            : 'border-[#E6E2D6] hover:border-[#C5A059]/50 hover:bg-[#C5A059]/5'
                        }`}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileInputChange}
                          className="hidden"
                          data-testid="file-input"
                        />
                        {uploading ? (
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
                            <p className="text-[#666660]">
                              {language === 'en' ? 'Uploading...' : 'جاري الرفع...'}
                            </p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
                            <p className="text-lg font-medium text-[#1A1A1A] mb-2">
                              {language === 'en' 
                                ? 'Drag & drop photos here' 
                                : 'اسحب وأفلت الصور هنا'}
                            </p>
                            <p className="text-sm text-[#666660] mb-4">
                              {language === 'en' 
                                ? 'or click to select from your device' 
                                : 'أو انقر للاختيار من جهازك'}
                            </p>
                            <p className="text-xs text-[#999]">
                              {language === 'en' 
                                ? 'Supports: JPG, PNG, WebP • Multiple files allowed' 
                                : 'يدعم: JPG, PNG, WebP • يمكنك اختيار عدة ملفات'}
                            </p>
                          </>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetGalleryForm}
                        className="border-[#E6E2D6] rounded-full"
                      >
                        {t.admin.cancel}
                      </Button>
                    </div>
                  ) : (
                    /* URL Mode */
                    <form onSubmit={handleSubmitGallery} className="space-y-4" data-testid="gallery-form">
                      <div className="space-y-2">
                        <Label>{language === 'en' ? 'Image URL' : 'رابط الصورة'}</Label>
                        <Input
                          value={galleryFormData.url}
                          onChange={(e) => setGalleryFormData({ ...galleryFormData, url: e.target.value })}
                          required
                          data-testid="input-gallery-url"
                          className="border-[#E6E2D6] focus:border-[#C5A059]"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'en' ? 'Caption (English)' : 'الوصف (إنجليزي)'}</Label>
                          <Input
                            value={galleryFormData.caption_en}
                            onChange={(e) => setGalleryFormData({ ...galleryFormData, caption_en: e.target.value })}
                            data-testid="input-gallery-caption-en"
                            className="border-[#E6E2D6] focus:border-[#C5A059]"
                            placeholder="Beautiful nail art"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'en' ? 'Caption (Arabic)' : 'الوصف (عربي)'}</Label>
                          <Input
                            value={galleryFormData.caption_ar}
                            onChange={(e) => setGalleryFormData({ ...galleryFormData, caption_ar: e.target.value })}
                            data-testid="input-gallery-caption-ar"
                            className="border-[#E6E2D6] focus:border-[#C5A059]"
                            placeholder="فن الأظافر الجميل"
                            dir="rtl"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'en' ? 'Category' : 'الفئة'}</Label>
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            onClick={() => setGalleryFormData({ ...galleryFormData, category: 'courses' })}
                            className={`rounded-full px-4 py-2 ${
                              galleryFormData.category === 'courses'
                                ? 'bg-[#C5A059] text-white'
                                : 'bg-white border border-[#E6E2D6] text-[#1A1A1A] hover:border-[#C5A059]'
                            }`}
                          >
                            {language === 'en' ? 'Courses & Training' : 'الدورات والتدريب'}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => setGalleryFormData({ ...galleryFormData, category: 'work' })}
                            className={`rounded-full px-4 py-2 ${
                              galleryFormData.category === 'work'
                                ? 'bg-[#C5A059] text-white'
                                : 'bg-white border border-[#E6E2D6] text-[#1A1A1A] hover:border-[#C5A059]'
                            }`}
                          >
                            {language === 'en' ? 'My Work' : 'أعمالي'}
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <Button
                          type="submit"
                          disabled={loading}
                          data-testid="save-gallery-btn"
                          className="bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
                        >
                          {language === 'en' ? 'Add Photo' : 'إضافة الصورة'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={resetGalleryForm}
                          data-testid="cancel-gallery-btn"
                          className="border-[#E6E2D6] rounded-full"
                        >
                          {t.admin.cancel}
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((image, index) => (
                <div
                  key={image.id}
                  data-testid={`admin-gallery-${index}`}
                  className="group relative overflow-hidden rounded-xl border border-[#E6E2D6] bg-white"
                >
                  {/* Image */}
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={getImageUrl(image.url)}
                      alt={language === 'en' ? image.caption_en : image.caption_ar}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Info & Controls */}
                  <div className="p-3 space-y-2">
                    {/* Caption */}
                    <p className="text-sm text-[#1A1A1A] font-medium truncate">
                      {language === 'en' ? (image.caption_en || 'No caption') : (image.caption_ar || 'بدون وصف')}
                    </p>
                    
                    {/* Category Selector */}
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleUpdateImageCategory(image.id, 'courses')}
                        data-testid={`category-courses-${index}`}
                        className={`flex-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                          image.category === 'courses'
                            ? 'bg-blue-500 text-white'
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                      >
                        {language === 'en' ? 'Training' : 'تدريب'}
                      </button>
                      <button
                        onClick={() => handleUpdateImageCategory(image.id, 'work')}
                        data-testid={`category-work-${index}`}
                        className={`flex-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                          image.category === 'work'
                            ? 'bg-purple-500 text-white'
                            : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                        }`}
                      >
                        {language === 'en' ? 'My Work' : 'أعمالي'}
                      </button>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingImage(image)}
                        data-testid={`edit-gallery-${index}`}
                        className="flex-1 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-full text-xs"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        {language === 'en' ? 'Edit' : 'تعديل'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteGalleryImage(image.id)}
                        data-testid={`delete-gallery-${index}`}
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Edit Image Modal */}
            {editingImage && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingImage(null)}>
                <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-playfair text-xl font-medium text-[#1A1A1A] mb-4">
                    {language === 'en' ? 'Edit Photo' : 'تعديل الصورة'}
                  </h3>
                  
                  {/* Preview */}
                  <div className="aspect-video rounded-lg overflow-hidden mb-4">
                    <img
                      src={getImageUrl(editingImage.url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Caption Inputs */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <Label className="text-sm">{language === 'en' ? 'Caption (English)' : 'الوصف (إنجليزي)'}</Label>
                      <Input
                        defaultValue={editingImage.caption_en}
                        id="edit-caption-en"
                        className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                        placeholder="Enter caption..."
                      />
                    </div>
                    <div>
                      <Label className="text-sm">{language === 'en' ? 'Caption (Arabic)' : 'الوصف (عربي)'}</Label>
                      <Input
                        defaultValue={editingImage.caption_ar}
                        id="edit-caption-ar"
                        className="border-[#E6E2D6] focus:border-[#C5A059] mt-1"
                        placeholder="أدخل الوصف..."
                        dir="rtl"
                      />
                    </div>
                  </div>
                  
                  {/* Category Selection */}
                  <div className="mb-4">
                    <Label className="text-sm mb-2 block">{language === 'en' ? 'Category' : 'الفئة'}</Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const captionEn = document.getElementById('edit-caption-en').value;
                          const captionAr = document.getElementById('edit-caption-ar').value;
                          handleUpdateImageCategory(editingImage.id, 'courses');
                          if (captionEn !== editingImage.caption_en || captionAr !== editingImage.caption_ar) {
                            handleUpdateImageCaption(editingImage.id, captionEn, captionAr);
                          }
                        }}
                        className={`flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all ${
                          editingImage.category === 'courses'
                            ? 'bg-blue-500 text-white'
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'
                        }`}
                      >
                        {language === 'en' ? 'Courses & Training' : 'الدورات والتدريب'}
                      </button>
                      <button
                        onClick={() => {
                          const captionEn = document.getElementById('edit-caption-en').value;
                          const captionAr = document.getElementById('edit-caption-ar').value;
                          handleUpdateImageCategory(editingImage.id, 'work');
                          if (captionEn !== editingImage.caption_en || captionAr !== editingImage.caption_ar) {
                            handleUpdateImageCaption(editingImage.id, captionEn, captionAr);
                          }
                        }}
                        className={`flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all ${
                          editingImage.category === 'work'
                            ? 'bg-purple-500 text-white'
                            : 'bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200'
                        }`}
                      >
                        {language === 'en' ? 'My Work' : 'أعمالي'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        const captionEn = document.getElementById('edit-caption-en').value;
                        const captionAr = document.getElementById('edit-caption-ar').value;
                        handleUpdateImageCaption(editingImage.id, captionEn, captionAr);
                      }}
                      className="flex-1 bg-[#C5A059] hover:bg-[#B08A3E] rounded-full"
                    >
                      {language === 'en' ? 'Save Changes' : 'حفظ التغييرات'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingImage(null)}
                      className="border-[#E6E2D6] rounded-full"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
